import ErrorAlert from "@/utility/ErrorAlert";
import http from "@/axios/http-common";
import {
  PpiRekapCakupan,
  PpiTrenMode,
  PpiUnitScope
} from "@/services/PpiBundleRekapBulananService";

export type PpiKepatuhanKelompok = "surveilans" | "audit";

export type PpiKepatuhanMode = PpiTrenMode;

export type PpiKepatuhanKategori = "baik" | "cukup" | "kurang" | "-";

export interface PpiKepatuhanAgregat {
  n: number;
  num: number;
  den: number;
  capaian: number | null;
  kategori: PpiKepatuhanKategori;
}

export interface PpiKepatuhanModulMeta {
  key: string;
  label: string;
  singkat: string;
  satuan: string;
  target: number;
  ambang: number;
}

export interface PpiKepatuhanRuangan {
  unit_id: string;
  unit_nama: string;
  ada_data: boolean;
  modul: Record<string, PpiKepatuhanAgregat>;
}

export interface PpiKepatuhanMatriks {
  kelompok: PpiKepatuhanKelompok;
  kelompok_label: string;
  mode: PpiKepatuhanMode;
  periode: string;
  periode_label: string;
  periode_prefix: string;
  cakupan: PpiRekapCakupan;
  cakupan_label: string;
  modul: PpiKepatuhanModulMeta[];
  ruangan: PpiKepatuhanRuangan[];
  jumlah: Record<string, PpiKepatuhanAgregat>;
  dihitung_pada: string;
  unit_scope: PpiUnitScope;
}

export interface PpiKepatuhanItem {
  key: string;
  label: string;
  num: number;
  den: number;
  capaian: number | null;
  kategori: PpiKepatuhanKategori;
}

export interface PpiKepatuhanBagian {
  key: string;
  label: string;
  keterangan: string | null;
  subtotal: PpiKepatuhanAgregat | null;
  items: PpiKepatuhanItem[];
}

export interface PpiKepatuhanModulRinci
  extends PpiKepatuhanAgregat,
    PpiKepatuhanModulMeta {
  bagian: PpiKepatuhanBagian[];
}

export interface PpiKepatuhanPerBangsal {
  kelompok: PpiKepatuhanKelompok;
  kelompok_label: string;
  mode: PpiKepatuhanMode;
  periode: string;
  periode_label: string;
  periode_prefix: string;
  unit_id: string;
  unit_nama: string;
  modul: PpiKepatuhanModulRinci[];
  dihitung_pada: string;
}

export interface PpiKepatuhanTitik {
  periode: string;
  label: string;
  singkat: string;
  modul: Record<string, PpiKepatuhanAgregat>;
}

export interface PpiKepatuhanTren {
  kelompok: PpiKepatuhanKelompok;
  kelompok_label: string;
  tren: PpiTrenMode;
  tahun: number;
  rentang_label: string;
  cakupan: PpiRekapCakupan | null;
  cakupan_label: string | null;
  unit_id: string | null;
  unit_nama: string | null;
  modul: PpiKepatuhanModulMeta[];
  titik: PpiKepatuhanTitik[];
  dihitung_pada: string;
}

function handle<T>(p: Promise<T>): Promise<T> {
  return p.catch(err => {
    ErrorAlert.showErrorAlert(err);
    return Promise.reject(err);
  });
}

const base = (kelompok: PpiKepatuhanKelompok) =>
  `/ppi/rekap-kepatuhan/${kelompok}`;

const matriks = (
  kelompok: PpiKepatuhanKelompok,
  mode: PpiKepatuhanMode,
  periode: string,
  cakupan: PpiRekapCakupan
) =>
  handle(
    http.getDataParam(`${base(kelompok)}/matriks`, {
      params: { mode, periode, cakupan }
    })
  );

const perBangsal = (
  kelompok: PpiKepatuhanKelompok,
  mode: PpiKepatuhanMode,
  periode: string,
  unitId?: string | null
) =>
  handle(
    http.getDataParam(`${base(kelompok)}/per-bangsal`, {
      params: { mode, periode, unit_id: unitId || undefined }
    })
  );

const tren = (
  kelompok: PpiKepatuhanKelompok,
  trenMode: PpiTrenMode,
  tahun: string,
  cakupan: PpiRekapCakupan,
  unitId?: string | null
) =>
  handle(
    http.getDataParam(`${base(kelompok)}/tren`, {
      params: { tren: trenMode, tahun, cakupan, unit_id: unitId || undefined }
    })
  );

export default {
  matriks,
  perBangsal,
  tren
};
